import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import api from '../utils/api';
import { clearOfflineDataOnLogout } from '../utils/clearOfflineDataOnLogout';

export const useAuthStore = create(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      _hasHydrated: false,

      login: async (email, password) => {
        try {
          const response = await api.post('/auth/login', { email, password });
          const { user, requiresOtp } = response.data.data;
          if (requiresOtp) {
            return { success: true, requiresOtp: true, email: response.data.data.email || email };
          }
          set({ user, isAuthenticated: true });
          const { clearOtherUsersInspectionEmergencyDrafts } = await import('../utils/inspectionEmergencyDraft');
          await clearOtherUsersInspectionEmergencyDrafts(user?.id);
          return { success: true };
        } catch (error) {
          const message = error.response?.data?.message || error.response?.data?.error?.message || error.message || 'Login failed';
          return { success: false, error: message };
        }
      },

      verifyLoginOTP: async (email, otp) => {
        try {
          const response = await api.post('/auth/verify-login-otp', { email, otp });
          const { user } = response.data.data;
          set({ user, isAuthenticated: true });
          const { clearOtherUsersInspectionEmergencyDrafts } = await import('../utils/inspectionEmergencyDraft');
          await clearOtherUsersInspectionEmergencyDrafts(user?.id);
          return { success: true };
        } catch (error) {
          const message = error.response?.data?.message || error.response?.data?.error?.message || error.message || 'Verification failed';
          return { success: false, error: message };
        }
      },

      register: async (userData) => {
        try {
          const response = await api.post('/auth/register', userData);
          const { user } = response.data.data;
          set({ user, isAuthenticated: true });
          const { clearOtherUsersInspectionEmergencyDrafts } = await import('../utils/inspectionEmergencyDraft');
          await clearOtherUsersInspectionEmergencyDrafts(user?.id);
          return { success: true };
        } catch (error) {
          return {
            success: false,
            error: error.response?.data?.message || 'Registration failed'
          };
        }
      },

      logout: async (options = {}) => {
        const { clearEmergencyDrafts = true } = options;
        try {
          await api.post('/auth/logout'); // Cookies sent via withCredentials
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          await clearOfflineDataOnLogout({ clearEmergencyDrafts });
          set({ user: null, isAuthenticated: false });
        }
      },

      refreshAccessToken: async () => {
        // Do not clear user here on failure — api interceptor captures emergency draft
        // then calls logout(), which clears session + offline data safely.
        await api.post('/auth/refresh'); // Refresh token from cookie; backend sets new cookies
      },

      updateUser: (userData) => {
        set((state) => ({
          user: { ...state.user, ...userData }
        }));
      }
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated
      }),
      onRehydrateStorage: () => (state, err) => {
        if (err) console.error('Auth rehydration failed:', err);
        // Defer so useAuthStore is defined (callback runs during store creation)
        queueMicrotask(() => {
          useAuthStore.setState({ _hasHydrated: true });
        });
      }
    }
  )
);

